import NextAuth, { User } from 'next-auth';
import type { Provider } from 'next-auth/providers';
import Google from 'next-auth/providers/google';
import Facebook from 'next-auth/providers/facebook';
import LinkedIn from 'next-auth/providers/linkedin';
import Credentials from 'next-auth/providers/credentials';
import { autoLoginCallback, credentialsCallback, impersonateCallback, liveMagicLinkCallback, magicLinkCallback, oAuthCallback, phoneOtpCallback } from '@auth/config_callbacks';
import { NEXTAUTH_SESSION_COOKIE_NAME } from '@auth/constants';
import { redirectCallback } from '@auth/redirects/redirectCallback';
import { encryptServerSide } from '@utils/encryption/server-side';

/**
 * @description Extends the NextAuth User interface to include the LoggedUserReadModel schema.
 */
declare module 'next-auth' {
  interface User extends ApiSchema<'LoggedUserReadModel'> {}
  interface Session {
    sessionId: string; // The encrypted API key used for server-side requests. Renamed from 'apiKey' to make it less obvious.
    provider: string; // The provider used for authentication, e.g., 'credentials', 'google', 'facebook', 'linkedin'.
    user: ApiSchema<'HeaderInfo'>; // The user data returned from the API, which includes the user's information like email, name, etc.
  }
}

/**
 * @description The authentication providers used in the application.
 */
const providers: Provider[] = [
  // Auth with email and password.
  Credentials({
    id: 'credentials',
    name: 'Credentials',
    credentials: {
      email: { label: 'Email', type: 'email' },
      password: { label: 'Password', type: 'password' },
      // Checkbox for 'I want a business account'. Value can be 'personal' or 'business'.
      walletType: { label: 'WalletType', type: 'text' },
      // Needed for the API headers, so we can get translated error messages.
      locale: { label: 'Locale', type: 'text' },
      // Use this to build the POST request URL for login or register. Value can be 'login' or 'register'.
      actionType: { label: 'ActionType', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => credentialsCallback(credentials),
  }),
  // Auth with magic link (user token).
  Credentials({
    id: 'magic-link',
    name: 'Magic-Link',
    credentials: {
      // Needed for the API headers, so we can get translated error messages.
      locale: { label: 'Locale', type: 'text' },
      // Use this to pass a user token for login (email or impersonation), if needed.
      userToken: { label: 'UserToken', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => magicLinkCallback(credentials),
  }),
  Credentials({
    id: 'impersonate',
    name: 'Impersonate',
    credentials: {
      // Needed for the API headers, so we can get translated error messages.
      locale: { label: 'Locale', type: 'text' },
      // Use this to pass a user token for login (email or impersonation), if needed.
      impersonateToken: { label: 'UserToken', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => impersonateCallback(credentials),
  }),
  Credentials({
    id: 'auto-login',
    name: 'Auto Login',
    credentials: {
      // Needed for the API headers, so we can get translated error messages.
      locale: { label: 'Locale', type: 'text' },
      // Use this to pass an auto token for automatic login
      userToken: { label: 'UserToken', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => autoLoginCallback(credentials),
  }),
  Credentials({
    id: 'live-magic-link',
    name: 'Live Magic Link',
    credentials: {
      shortId: { label: 'ShortId', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => liveMagicLinkCallback(credentials),
  }),
  Credentials({
    id: 'phone-otp',
    name: 'Phone OTP',
    credentials: {
      // Translated error messages from the API.
      locale: { label: 'Locale', type: 'text' },
      // Single-use token issued by /v1/job/{jobSlug}/phone-apply/verify
      // and swapped for an API session at /v1/openapi_authenticate/phone-otp.
      token: { label: 'Token', type: 'text' },
    },
    authorize: async (credentials): Promise<User | null> => phoneOtpCallback(credentials),
  }),
  Facebook({
    issuer: 'https://www.facebook.com',
    token: {
      url: 'https://graph.facebook.com/v19.0/oauth/access_token',
      async conform(response: Response) {
        const body = (await response.json()) as Record<string, unknown>;
        if (typeof body === 'object' && body !== null && 'id_token' in body) {
          delete (body as { [key: string]: unknown }).id_token;
        }
        return new Response(JSON.stringify(body));
      },
    },
    profile: async (profile, tokens) => oAuthCallback(tokens, 'facebook'),
  }),
  Google({
    profile: async (profile, tokens) => oAuthCallback(tokens, 'google'),
  }),
  LinkedIn({
    profile: async (profile, tokens) => oAuthCallback(tokens, 'linkedin'),
    authorization: { params: { scope: 'openid profile email' } },
  }),
];

/**
 * @description The authentication providers map used in the login page.
 */
export const providerMap = providers
  .map((provider) => {
    if (typeof provider === 'function') {
      const providerData = provider();
      return {
        id: providerData.id,
        name: providerData.name,
      };
    }
    return {
      id: provider.id,
      name: provider.name,
    };
  })
  .filter((provider) => provider.id !== 'credentials' && provider.id !== 'live-magic-link' && provider.id !== 'phone-otp');

/**
 * @description The main entry point for the authentication system.
 */
export const { handlers, auth, signIn, signOut } = NextAuth({
  providers,
  pages: {
    signIn: '/login',
    newUser: '/register',
  },
  session: {
    strategy: 'jwt',
    maxAge: 60 * 60 * 24 * 182, // 1/2 year
  },
  cookies: {
    sessionToken: {
      name: NEXTAUTH_SESSION_COOKIE_NAME, // short for 'auth session token' => ast
      options: {
        httpOnly: true,
        domain: process.env.NEXT_PUBLIC_LOCALE_COOKIE_DOMAIN,
      },
    },
    callbackUrl: {
      name: 'auth_callbackUrl',
      options: {
        httpOnly: true,
        domain: process.env.NEXT_PUBLIC_LOCALE_COOKIE_DOMAIN,
      },
    },
    csrfToken: {
      name: 'auth_csrfToken',
      options: {
        httpOnly: true,
        domain: process.env.NEXT_PUBLIC_LOCALE_COOKIE_DOMAIN,
      },
    },
  },
  callbacks: {
    async jwt({
      token, user, account, trigger, session,
    }) {
      if (user) {
        // Encrypt the API key server-side for security
        // This is necessary to prevent exposing the API key in the client-side code.
        // Decrypt it server-side when needed. Use the `decryptServerSide` function for this.
        token.apiKey = user.apiKey ? encryptServerSide(user.apiKey) : null;
        token.headerInfo = user.headerInfo;

        if (account?.provider === 'credentials') {
          token.credentials = true;
        }

        if (account?.provider === 'google' || account?.provider === 'facebook' || account?.provider === 'linkedin') {
          token.provider = account.provider;
          token.code = account.code;
          token.accessToken = account.access_token;
        }
      }

      // Client-driven self-heal (useHeaderInfoData calls update({ ... })): the login payload freezes
      // the employer flags into the JWT, but they can be stale for some company accounts and
      // mis-route the /my-jobs hub. Patch the live values in when they diverge.
      //
      // `hasEmployerAccount` is the load-bearing one for old sessions: it did not exist when their
      // JWT was minted, and sessions last 182 days, so without this patch those users would keep
      // falling through to the /my-jobs job probe for up to half a year.
      if (trigger === 'update' && session && typeof session === 'object' && token.headerInfo) {
        const patch = session as { hasBusinessWallet?: boolean; hasEmployerAccount?: boolean };
        const headerInfo = token.headerInfo as { hasBusinessWallet?: boolean; hasEmployerAccount?: boolean };

        if (typeof patch.hasBusinessWallet === 'boolean') {
          headerInfo.hasBusinessWallet = patch.hasBusinessWallet;
        }
        if (typeof patch.hasEmployerAccount === 'boolean') {
          headerInfo.hasEmployerAccount = patch.hasEmployerAccount;
        }
      }
      return token;
    },
    async session({ session, token }) {
      // Extend the session with what we need
      if (token) {
        session.user = token.headerInfo as never;
        session.provider = (token?.provider as string | undefined) || 'credentials';
        // The apiKey, renamed and encrypted for security.
        // Use the `decryptServerSide` function in route handlers for decryption.
        session.sessionId = token.apiKey as string;
      }
      return session;
    },
    async redirect({ url, baseUrl }) {
      return redirectCallback(url, baseUrl);
    },
  },
});
